
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { PortfolioType } from '@/types/portfolio';

interface CreatePortfolioDialogProps {
  onCreate: (portfolio: {
    title: string;
    description: string;
    portfolio_type: PortfolioType;
  }) => void;
}

export const CreatePortfolioDialog = ({ onCreate }: CreatePortfolioDialogProps) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [portfolioType, setPortfolioType] = useState<PortfolioType>('buildings');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) return;
    
    onCreate({
      title,
      description,
      portfolio_type: portfolioType,
    });
    
    // Reset form
    setTitle('');
    setDescription('');
    setPortfolioType('buildings');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create Portfolio
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Portfolio</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Portfolio Title"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="portfolio-type">Portfolio Type</Label>
            <Select 
              value={portfolioType} 
              onValueChange={(value) => setPortfolioType(value as PortfolioType)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a portfolio type" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Portfolio Types</SelectLabel>
                  <SelectItem value="buildings">Buildings</SelectItem>
                  <SelectItem value="formulated_products">Formulated Products</SelectItem>
                  <SelectItem value="interior_spaces">Interior Spaces</SelectItem>
                  <SelectItem value="paper_rolls">Paper Rolls</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your portfolio"
              rows={3}
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">Create Portfolio</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
